import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Copy, Check } from 'lucide-react';
import { apiClient } from '@/lib/apiClient';
import { Button } from '@/components/ui/Button';

// ---- Types ----

interface ChatWidgetConfig {
  id: string;
  tenantId: string;
  allowedOrigins: string[];
  welcomeMessage: string;
  brandingLogoUrl: string | null;
  widgetColor: string;
  enabled: boolean;
  updatedAt: string;
}

interface FormState {
  allowedOrigins: string; // newline-separated in the textarea
  welcomeMessage: string;
  brandingLogoUrl: string;
  widgetColor: string;
  enabled: boolean;
}

// ---- Component ----

export function ChatWidgetPage() {
  const { t } = useTranslation();
  const [config, setConfig] = useState<ChatWidgetConfig | null>(null);
  const [form, setForm] = useState<FormState>({
    allowedOrigins: '',
    welcomeMessage: 'Hi! How can we help you today?',
    brandingLogoUrl: '',
    widgetColor: '#6366f1',
    enabled: true,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<ChatWidgetConfig>('/chat-widget/config');
      setConfig(data);
      setForm({
        allowedOrigins: data.allowedOrigins.join('\n'),
        welcomeMessage: data.welcomeMessage,
        brandingLogoUrl: data.brandingLogoUrl ?? '',
        widgetColor: data.widgetColor,
        enabled: data.enabled,
      });
    } catch {
      // 404 = no config yet; start with defaults
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 3000);
  }

  async function handleSave() {
    setSaving(true);
    try {
      const payload = {
        allowedOrigins: form.allowedOrigins
          .split('\n')
          .map((s) => s.trim())
          .filter(Boolean),
        welcomeMessage: form.welcomeMessage,
        brandingLogoUrl: form.brandingLogoUrl || null,
        widgetColor: form.widgetColor,
        enabled: form.enabled,
      };
      const { data } = await apiClient.put<ChatWidgetConfig>('/chat-widget/config', payload);
      setConfig(data);
      showToast(t('chatWidget.saved'));
    } catch {
      showToast(t('chatWidget.error_save'));
    } finally {
      setSaving(false);
    }
  }

  // Build embed snippet — derive slug from tenant; we use a placeholder here
  // since the actual slug comes from the auth context.
  const tenantSlug = config?.tenantId
    ? `your-tenant-slug`
    : 'your-tenant-slug';

  const embedSnippet = `<script>
  (function(w,d,s){
    var j=d.createElement(s);j.async=true;
    j.src='${window.location.origin}/embed/chat.js';
    j.dataset.tenant='${tenantSlug}';
    j.dataset.color='${form.widgetColor}';
    d.head.appendChild(j);
  })(window,document,'script');
</script>`;

  async function copyEmbed() {
    await navigator.clipboard.writeText(embedSnippet);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (loading) {
    return <p className="text-sm text-muted-foreground">{t('common.loading')}</p>;
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      {toast && (
        <div className="fixed right-6 top-20 z-50 rounded-lg border bg-card px-4 py-2 shadow-lg text-sm">
          {toast}
        </div>
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold">{t('chatWidget.title')}</h1>
        <p className="text-sm text-muted-foreground">{t('chatWidget.subtitle')}</p>
      </div>

      {/* Config form */}
      <div className="space-y-4 rounded-lg border bg-card p-6">
        {/* Enabled toggle */}
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="widget-enabled"
            checked={form.enabled}
            onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
            className="h-4 w-4 rounded border"
          />
          <label htmlFor="widget-enabled" className="text-sm font-medium">
            {t('chatWidget.enabled')}
          </label>
        </div>

        {/* Welcome message */}
        <div>
          <label className="mb-1 block text-sm font-medium">{t('chatWidget.welcome_message')}</label>
          <input
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            value={form.welcomeMessage}
            onChange={(e) => setForm((f) => ({ ...f, welcomeMessage: e.target.value }))}
          />
        </div>

        {/* Widget color */}
        <div>
          <label className="mb-1 block text-sm font-medium">{t('chatWidget.widget_color')}</label>
          <div className="flex items-center gap-2">
            <input
              type="color"
              className="h-9 w-16 cursor-pointer rounded border"
              value={form.widgetColor}
              onChange={(e) => setForm((f) => ({ ...f, widgetColor: e.target.value }))}
            />
            <input
              className="flex-1 rounded-md border bg-background px-3 py-2 text-sm"
              value={form.widgetColor}
              onChange={(e) => setForm((f) => ({ ...f, widgetColor: e.target.value }))}
            />
          </div>
        </div>

        {/* Logo URL */}
        <div>
          <label className="mb-1 block text-sm font-medium">{t('chatWidget.branding_logo_url')}</label>
          <input
            className="w-full rounded-md border bg-background px-3 py-2 text-sm"
            placeholder="https://..."
            value={form.brandingLogoUrl}
            onChange={(e) => setForm((f) => ({ ...f, brandingLogoUrl: e.target.value }))}
          />
        </div>

        {/* Allowed origins */}
        <div>
          <label className="mb-1 block text-sm font-medium">{t('chatWidget.allowed_origins')}</label>
          <p className="mb-1 text-xs text-muted-foreground">{t('chatWidget.allowed_origins_hint')}</p>
          <textarea
            className="w-full rounded-md border bg-background px-3 py-2 text-sm font-mono"
            rows={4}
            placeholder="https://example.com&#10;https://app.example.com"
            value={form.allowedOrigins}
            onChange={(e) => setForm((f) => ({ ...f, allowedOrigins: e.target.value }))}
          />
        </div>

        <Button onClick={handleSave} disabled={saving}>
          {saving ? t('common.loading') : t('chatWidget.save')}
        </Button>
      </div>

      {/* Embed snippet */}
      <div className="rounded-lg border bg-card p-6">
        <h2 className="mb-1 font-semibold">{t('chatWidget.embed_title')}</h2>
        <p className="mb-3 text-xs text-muted-foreground">{t('chatWidget.embed_hint')}</p>
        <pre className="overflow-x-auto rounded-md bg-muted p-4 text-xs">
          <code>{embedSnippet}</code>
        </pre>
        <Button size="sm" variant="outline" className="mt-3" onClick={copyEmbed}>
          {copied ? (
            <>
              <Check className="mr-1.5 h-3.5 w-3.5 text-emerald-600" />
              {t('chatWidget.copied')}
            </>
          ) : (
            <>
              <Copy className="mr-1.5 h-3.5 w-3.5" />
              {t('chatWidget.copy')}
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
